import React from 'react'
// libs
import { css } from 'styled-components'
import { useRouter } from 'hooks/useRouter'
import { useMutation } from '@apollo/client/react'
// components
import { DetailsButton } from 'components/UI'
import ConfirmDeleteModal from 'components/Modals/ConfirmDeleteModal'
// context
import { ObjectiveContext } from 'context/index'
import { PlanEditPageContext } from 'containers/Plan/Edit/layers/API'
import { ObjectivesPageContext } from 'containers/Objectives/Register/layers/API'
// constants
import { Modals } from 'constants/index'
// graphql
import { PLAN_OBJECTIVE_DELETE_MUTATION } from 'graphql/planObjectives/mutations'
import { PLAN_ITEM_SET_ARCHIVED_MUTATION } from 'graphql/plan/mutations'
// icons
import ColorIcon from '@mui/icons-material/ColorLensOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined'
import BookmarkIcon from '@mui/icons-material/BookmarkBorderOutlined'
import UnarchiveIcon from '@mui/icons-material/Unarchive'
// types
import { ISecurityProfilePermissions } from 'types/graphql'
// hooks
import { useSnackbar } from 'notistack'
import { usePlanItemColorPicker } from 'hooks/usePlanItemColorPicker'
import { useBookmarks, useModalData, useShowModal } from 'hooks'
// utils
import get from 'lodash/get'
import { handleError } from 'utils/errors/handlers'
import { getSnackbarCloseButton } from 'providers/SnackbarProvider/utils'

export const DetailsButtonCSS = css`
  position: absolute;
  right: 16px;
  top: 0;
  z-index: 1;
`

interface Props {
  hideParent: () => void
}

const PlanObjectiveActionsMenu: React.FC<Props> = ({ hideParent }) => {
  const router = useRouter()

  const { entity, refetch, update } = React.useContext(ObjectiveContext)

  const { refetch: planObjectiveListRefetch } = React.useContext(ObjectivesPageContext)
  const { treeQueryRefetch } = React.useContext(PlanEditPageContext)

  const { objectiveDeleteObjective, objectiveArchiveObjective } =
    entity.currentPermissions as ISecurityProfilePermissions

  const { enqueueSnackbar, closeSnackbar } = useSnackbar()

  const [deleteMutation] = useMutation(PLAN_OBJECTIVE_DELETE_MUTATION)
  const [archiveMutation] = useMutation(PLAN_ITEM_SET_ARCHIVED_MUTATION)

  const { showModal } = useShowModal({ modalType: Modals.CONFIRM_DELETE_OBJECTIVE_MODAL })
  const { modalData } = useModalData<{ color?: string }>({ modalType: Modals.OBJECTIVE_MINIMIZED_MODAL })

  const shouldArchive = !entity.archived && objectiveArchiveObjective

  const createOrDeleteBookmark = useBookmarks()

  const handleDeleteClick = () => {
    if (get(entity, 'planItems.data').length > 1) {
      return enqueueSnackbar('Record can not be deleted as it is referenced by another record!', {
        variant: 'warning',
        action: (key) => getSnackbarCloseButton(key, closeSnackbar)
      })
    }
    const planItemId = get(entity, 'planItems.data[0].id')

    showModal({
      width: 'sm',
      handleConfirm: async () => {
        try {
          if (shouldArchive) {
            await archiveMutation({ variables: { id: planItemId, archived: true } })
          } else {
            await deleteMutation({ variables: { id: entity.id } })
          }

          await treeQueryRefetch?.()
          await planObjectiveListRefetch?.()

          hideParent()
        } catch (errors) {
          handleError(errors, 'PlanObjectiveActionsMenu.handleDeleteClick')
        }
      }
    })
  }

  const handleSetColorClick = usePlanItemColorPicker({ entity, update, defaultColor: modalData.color })

  const onCreateOrDeleteBookmark = async () => {
    const input = {
      planObjectiveId: entity.id
    }

    await createOrDeleteBookmark(input, entity.isBookmark)

    refetch()

    if (router.query.slug?.[0] === 'bookmarks') {
      planObjectiveListRefetch?.()
    }
  }

  const handleUnarchiveClick = async () => {
    const planItemId = get(entity, 'planItems.data[0].id')

    try {
      await archiveMutation({ variables: { id: planItemId, archived: false } })
      await treeQueryRefetch?.()
    } catch (errors) {
      handleError(errors, 'PlanObjectiveActionsMenu.handleUnarchiveClick')
    }
  }

  return (
    <>
      <DetailsButton styles={DetailsButtonCSS}>
        <span onClick={onCreateOrDeleteBookmark}>
          <BookmarkIcon />
          {entity.isBookmark ? 'Remove from bookmarks' : 'Add to bookmarks'}
        </span>
        <span onClick={handleSetColorClick}>
          <ColorIcon />
          Set Color
        </span>
        {objectiveArchiveObjective && entity.archived && (
          <span onClick={handleUnarchiveClick}>
            <UnarchiveIcon />
            Unarchive
          </span>
        )}
        {(objectiveArchiveObjective || objectiveDeleteObjective) && (
          <span onClick={handleDeleteClick}>
            <DeleteIcon />
            {shouldArchive ? 'Archive' : 'Delete'}
          </span>
        )}
      </DetailsButton>
      <ConfirmDeleteModal
        header={shouldArchive ? `Archive ${entity.description}` : `Delete ${entity.description}`}
        modalType={Modals.CONFIRM_DELETE_OBJECTIVE_MODAL}>
        {shouldArchive ? (
          'Are you sure you want to archive this plan objective? This will also archive any sub-items.'
        ) : (
          <span>
            Are you sure you want to delete this plan objective? This will also <b>delete</b> any sub-items.
          </span>
        )}
      </ConfirmDeleteModal>
    </>
  )
}

export default PlanObjectiveActionsMenu
